import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import EditCountry from "./partials/EditRole/EditRole";
import DeleteCountry from "./partials/DeleteCountry/DeleteCountry";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import AddRole from "./partials/AddRole/AddRoles";
import { useTranslation } from "react-i18next";
import {
  addPermissionList,
  deletePermissionList,
  editPermissionList,
  getPermissionList,
  PermissionsCells,
} from "framework/permissions";
import {
  IPermission,
  IPermissionList,
  IPermissionListInput,
} from "models/permissions";
import { getPermission, getUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
interface Props {
  feature: Feature;
}

const Permissions: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [loading3, setLoading3] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [Roles, setRoles] = useState<IPermissionList[]>(
    [] as IPermissionList[]
  );
  const [FilterRoles, setFilterRoles] = useState<IPermissionList[]>(
    [] as IPermissionList[]
  );
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<IPermissionList>(
    {} as IPermissionList
  );
  const user = getUserData();
  const { colors } = user;
  const [inputObj, setInputObj] = useState<IPermissionListInput>(
    {} as IPermissionListInput
  );
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addCountriesMutation = useMutation({
    mutationFn: (createInput: IPermissionListInput) => {
      return addPermissionList(createInput);
    },
  });

  const editCountriesMutation = useMutation({
    mutationFn: (data: { createInput: IPermissionListInput; id: string }) => {
      return editPermissionList(data.createInput, data.id);
    },
  });

  const deleteCountriesMutation = useMutation({
    mutationFn: (id: string) => {
      return deletePermissionList(id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery("get Roles", () =>
        getPermissionList()
      );
      response = response = response && response.length > 0 ? response : [];
      setRoles(response);
      setFilterRoles(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(7);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Role: IPermissionList) => {
    setSelectedRole(Role);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedRole({} as IPermissionList);
  };

  const handleOpenDelete = (Role: IPermissionList) => {
    setSelectedRole(Role);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedRole({} as IPermissionList);
  };

  const getBodyCells = (row: IPermissionList) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "description")
        return {
          id: "description",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.description}</Typography>
            </TableCell>
          ),
        };

      if (key === "descriptionAr")
        return {
          id: "descriptionAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.descriptionAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (
      !inputObj.name ||
      !inputObj.description ||
      !inputObj.nameAr ||
      !inputObj.descriptionAr
    )
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addCountriesMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Role added successfully",
        type: "success",
      });
      setInputObj({} as IPermissionListInput);
      fetchData();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    // if (!inputObj.name)
    //   return setAlert({
    //     open: true,
    //     message: "Must provide a name",
    //     type: "error",
    //   });
    try {
      setLoading2(true);
      let createInput: IPermissionListInput = {
        name: inputObj.name || selectedRole.name,
        description: inputObj.description || selectedRole.description,
        nameAr: inputObj.nameAr || selectedRole.nameAr,
        descriptionAr: inputObj.descriptionAr || selectedRole.descriptionAr,
      };
      await editCountriesMutation.mutateAsync({
        createInput,
        id: selectedRole.id,
      });
      setAlert({
        open: true,
        message: "Permission updated successfully",
        type: "success",
      });
      setInputObj({} as IPermissionListInput);
      fetchData();
      handleCloseEdit();

      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCountriesMutation.mutateAsync(selectedRole.id);
      setAlert({
        open: true,
        message: "Permission deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterRoles(Roles);
    let filtered = Roles.filter((role) =>
      role.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilterRoles(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title="Add Role">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterRoles.length > 0 && (
          <List
            headCells={PermissionsCells}
            data={FilterRoles}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}

        {(!FilterRoles || FilterRoles.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_permissions")}
              message={t("T_no_permissions_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddRole
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditCountry
          open={openEdit}
          loading={loading2}
          permission={selectedRole}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteCountry
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Permissions;
